// What a LAUNCH request may cost, and what a timeout on one means.
//
// Both launch doors — the COP queue panel's per-row LAUNCH and the compose
// panel's commit strip — post to `POST /api/missions/:id/launch` and wait on
// the same server work. So the ceiling and the copy live here once: two doors
// that disagree about how long a launch may take are two doors that report
// different outcomes for the same sortie.

/**
 * Ceiling on a launch request.
 *
 * Bounded because a black-holed request otherwise leaves the commanding
 * control disabled forever — the operator's only recovery is a reload, which
 * on the compose panel destroys a route that exists nowhere else.
 *
 * The number is NOT free. A launch from a dock holds the request open for the
 * whole bay sequence, which the server bounds at
 * `BAY_COMMAND_GAP_MS` (3s) + `BAY_MOVE_TIMEOUT_HTTP_MS` (45s) = 48s before
 * the mission upload is even attempted — the hardware takes "about 30 seconds"
 * (`src/services/dock-launch-sequence.ts`). A ceiling below that aborts EVERY
 * successful dock launch and reports it as INDETERMINATE, which is the one
 * verdict that sends an operator to the airfield to check.
 *
 * The upper bound is the proxy: nginx has no `proxy_read_timeout` on
 * `location /api/`, so its 60s default cuts the connection and returns an
 * opaque 504. Waiting past that buys nothing — there is no longer anyone to
 * answer. 55s sits above the bay budget and below the proxy's cut, so a dock
 * refusal (`bay_not_open`, with the tray value and a real reason) arrives at
 * the operator instead of a timeout.
 *
 * WHAT THIS DOES NOT COVER, deliberately: after the bay opens, `uploadMission`
 * and `startMission` each wait up to `MQTT_PUBLISH_TIMEOUT_MS` (10s) for a
 * broker ack, so the true worst case is ~68s — past this ceiling AND past
 * nginx's. Sizing for it is not possible without raising `proxy_read_timeout`
 * first, and it would mean holding a wedged control for over a minute to buy a
 * tail that a healthy broker answers in milliseconds. When it does happen the
 * operator gets INDETERMINATE, which is the honest verdict: a publish that
 * timed out may still be replayed to the aircraft (see MqttPublishTimeoutError).
 *
 * If `BAY_MOVE_TIMEOUT_HTTP_MS`, `MQTT_PUBLISH_TIMEOUT_MS` or
 * `proxy_read_timeout` moves, this moves with them — see
 * `deploy/nginx/README.md`, which records the relationship.
 *
 * THE THIRD DOOR CARRIES LESS MARGIN THAN THE TWO ABOVE, and this is the note
 * rather than a second constant because there is nowhere for a bigger number to
 * go — nginx's 60s default is the hard bound. `POST /api/missions/dispatch`
 * (Quick Dispatch) reaches the same bay sequence, but does strictly MORE work
 * first: it resolves the drone, resolves an origin (Redis, then `last_telemetry`,
 * then the dock row), reads the org's altitude ceiling, and CREATES the mission
 * and its waypoints — none of which the other two doors do, because they launch a
 * mission that already exists. On a bay that takes the full 50s, that head start
 * can put the server's answer past this ceiling, and the operator gets
 * INDETERMINATE instead of the `bay_not_open` refusal that carries the tray value
 * and routes them to the dock panel's visual-confirm. Not silent and not unsafe —
 * INDETERMINATE tells them to verify — but it is the worse of two true answers.
 */
export const LAUNCH_REQUEST_TIMEOUT_MS = 55_000;

/**
 * A timed-out launch is INDETERMINATE — the command may or may not have
 * reached the aircraft (same doctrine as f0b3612: a timeout is indeterminate
 * at both doors). The copy must send the operator to verify, never to
 * blind-retry.
 */
export const LAUNCH_TIMEOUT_MESSAGE =
  'Launch request timed out — the launch state is INDETERMINATE. Verify the aircraft before commanding again.';

/**
 * What a timed-out WRITE on the way to a launch means.
 *
 * Same doctrine as the launch itself, one step earlier: aborting a fetch does not cancel the
 * request the server is already handling, so "Failed to create mission" is a claim the client
 * cannot make. It can be worse than wrong — a create whose POST actually landed leaves no id in
 * hand, so the next press creates a SECOND mission row for the same sortie.
 */
export const COMMIT_TIMEOUT_MESSAGE =
  'The server did not answer in time. The write may or may not have landed — check the missions board before trying again.';

/**
 * Did this rejection come from the abort signal rather than the server?
 *
 * `AbortSignal.timeout` rejects with `TimeoutError`; an explicit abort with
 * `AbortError`. Both mean the same thing to an operator: nobody heard the
 * answer, so the outcome is unknown.
 */
export function isLaunchRequestTimeout(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError');
}

/**
 * The line a successful launch adds when something did not block it.
 *
 * Advisory airspace conflicts are returned on the 202 (`advisories`) rather than only logged.
 * Since a same-aircraft clash with a merely SCHEDULED mission stopped being a refusal, the
 * refusal is no longer there to carry it — so an operator could launch an airframe another
 * operator had planned a sortie for, kill that plan, and neither of them would ever be told.
 *
 * Returns null when there is nothing to say, which is the normal case.
 */
export function launchAdvisoryNote(advisories: unknown): string | null {
  if (!Array.isArray(advisories)) return null;
  const lines = advisories.filter((a): a is string => typeof a === 'string' && a.trim() !== '');
  return lines.length === 0 ? null : lines.join(' · ');
}
